'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getApolloClient } from '@frameio/graphql-client';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { GetNotificationGroups } from '../gql';

type ShouldShowTruncatedAccessRequestsListContextType = {
  shouldShowTruncatedAccessRequestsList: boolean;
  setShouldShowTruncatedAccessRequestsList: (value: boolean) => void;
};

const ShouldShowTruncatedAccessRequestsListContext =
  createContext<ShouldShowTruncatedAccessRequestsListContextType>({
    shouldShowTruncatedAccessRequestsList: true,
    setShouldShowTruncatedAccessRequestsList: () => undefined,
  });

export const useShouldShowTruncatedAccessRequestsList = () => {
  const context = useContext(ShouldShowTruncatedAccessRequestsListContext);
  if (!context) {
    throw new Error(
      'useShouldShowTruncatedAccessRequestsList must be used within an ShouldShowTruncatedAccessRequestsListProvider'
    );
  }
  return context;
};

/**
 * This context determines whether to show a truncated version of the access
 * requests list based on whether notifications exist and whether the total number of access requests
 * exceeds the maximum number of items that can be displayed before
 * notifications are pushed below the fold. This state will be reset to its
 * default state when the notification panel is closed, when the refresh chip
 * is clicked or when the active account ID changes.
 */
export const ShouldShowTruncatedAccessRequestsListProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const accountId = useActiveAccountId();
  const client = getApolloClient();

  const [
    shouldShowTruncatedAccessRequestsList,
    setShouldShowTruncatedAccessRequestsList,
  ] = useState(true);

  useEffect(() => {
    if (!accountId) {
      return;
    }

    const fetchNotificationGroups = async () => {
      const notificationGroupsData = await client.query({
        query: GetNotificationGroups,
        variables: {
          accountId,
          page: {
            first: 1,
          },
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });

      const totalCountNotifications =
        notificationGroupsData?.data?.account?.notificationGroups?.nodes.length;
      const hasNotifications =
        typeof totalCountNotifications === 'number' &&
        totalCountNotifications > 0;

      setShouldShowTruncatedAccessRequestsList(hasNotifications);
    };

    fetchNotificationGroups();
  }, [accountId, client]);

  const value = useMemo(
    () => ({
      shouldShowTruncatedAccessRequestsList,
      setShouldShowTruncatedAccessRequestsList,
    }),
    [shouldShowTruncatedAccessRequestsList]
  );

  return (
    <ShouldShowTruncatedAccessRequestsListContext.Provider value={value}>
      {children}
    </ShouldShowTruncatedAccessRequestsListContext.Provider>
  );
};
